import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  Chart,
  ChartConfiguration,
  ChartData as ChartJsData,
  ChartOptions,
  Plugin
} from 'chart.js'; // alias per evitare conflitti

@Component({
  selector: 'app-generic-chart-widget',
  templateUrl: './generic-chart-widget.component.html',
  styleUrls: ['./generic-chart-widget.component.css']
})
export class GenericChartWidgetComponent implements AfterViewInit, OnChanges {
  @Input() chartPlugin: Plugin | null = null;
  @Input() chartData: ChartJsData;
  @Input() chartOptions: ChartOptions;
  @Input() chartType: ChartConfiguration['type'];
  @Input() chartId: string =
    `chart-${Math.random().toString(36).substring(2, 9)}`;
  @ViewChild('chartCanvas', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;
  chart: Chart;

  private readonly fallbackColors = [
    'rgb(54, 162, 235)',
    'rgb(255, 159, 64)',
    'rgb(153, 102, 255)',
    'rgb(75, 192, 192)',
    'rgb(255, 205, 86)',
    'rgb(201, 203, 207)',
    'rgb(255, 99, 132)',
    'rgb(99, 255, 104)'
  ];

  get safeChartData(): ChartJsData | undefined {
    if (!this.chartData) return undefined;
    return {
      ...this.chartData,
      datasets: (this.chartData.datasets ?? []).map((ds, i) => {
        const color = this.fallbackColors[i % this.fallbackColors.length];
        return {
          backgroundColor: color,
          borderColor: color,
          ...ds
        };
      })
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['chartData'] ||
      changes['chartOptions'] ||
      changes['chartType']
    ) {
      // Aggiorna il grafico se i dati o le opzioni cambiano
      this.updateChart();
    }
  }

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null as any; // Rimuovi il riferimento
    }
  }

  private initChart(): void {
    if (!this.chartCanvas) return;
    const ctx: CanvasRenderingContext2D | null =
      this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Impossibile ottenere il contesto canvas');
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: this.chartType,
      data: this.chartData,
      options: { ...this.defaultChartOptions, ...this.chartOptions },
      plugins: this.chartPlugin ? [this.chartPlugin] : undefined
    });
  }

  private updateChart(): void {
    if (this.chart) {
      this.chart.data = this.chartData;
      this.chart.options = {
        ...this.defaultChartOptions,
        ...this.chartOptions
      };
      this.chart.update();
    }
  }

  defaultChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        enabled: true
      }
    }
  };
}
